import React, { Component } from 'react';
import { withRouter } from 'react-router-dom';

import App from 'App';
import API from 'API';
import TextHelpers from 'helpers/TextHelpers';

// Components
import Loader from 'components/common/Loader';
import FindProperty from 'components/steps/FindProperty';
import NoAccount from 'components/steps/NoAccount';
import PersonalDetails from 'components/steps/PersonalDetails';
import ChimneyDetails from 'components/steps/ChimneyDetails';
import ChimneyList from 'components/steps/ChimneyList';
import ConfirmPrice from 'components/steps/ConfirmPrice';
import SelectDateAndTime from 'components/steps/SelectDateAndTime';
import Summary from 'components/steps/Summary';
import Thanks from 'components/steps/Thanks';

//-------------------------------------------------------------------------------------------------------------------

class BookAppt extends Component {
    
    constructor(props) {
        super(props);

        this.tryLoadAccountInfo = this.tryLoadAccountInfo.bind(this);
        this.goToStep = this.goToStep.bind(this);

        let bookingRequest = this.loadFromLocalStorage();
        bookingRequest ||= {
            accountID: null,
            houseNumber: null,
            postcode: null,
            customerFirstName: null,
            customerLastName: null,
            customerEmail: null,
            customerTel: null,
            chimneyTypeIDs: [],
            version: App.VERSION
        };
        if (!bookingRequest.sessionID) {
            bookingRequest.sessionID = TextHelpers.generateRandomString(16);
        }

        // Update to latest version
        // 1.24 -> 1.25: allow multiple chimneys
        if (!bookingRequest.chimneyTypeIDs) {
            bookingRequest.chimneyTypeIDs = [];
            if (bookingRequest.chimneyTypeID) {
                bookingRequest.chimneyTypeIDs.push(bookingRequest.chimneyTypeID);
                delete bookingRequest.chimneyTypeID;
            }
        }

        this.saveToLocalStorage(bookingRequest);
        
        this.state = {
            bookingRequest,
            isLoading: true
        };
    }

    componentDidMount() {
        // Load account info first, then validate current step and redirect if invalid
        (async () => {
            await this.tryLoadAccountInfo();
            this.ensureValidStep();
        })();
    }

    componentDidUpdate(oldProps) {
        if (this.props.stepName != oldProps.stepName) {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Validate whenever the step changes and redirect back to the first step if invalid
            this.ensureValidStep();
        }
    }

    // Validate the bookingRequest for the given stepName.
    // Returns null if the current step is valid, otherwise returns the step name
    // the user should be redirected to.
    validateBookingRequest() {
        const { stepName } = this.props;
        const { bookingRequest } = this.state;
        if (!bookingRequest) return 'findProperty';

        // Helper checks
        const hasPostcode = !!bookingRequest.postcode;
        const hasName = !!(bookingRequest.customerFirstName && bookingRequest.customerLastName);
        const hasChimneys = !!(bookingRequest.chimneyTypeIDs && bookingRequest.chimneyTypeIDs.length > 0);

        switch (stepName) {
            case 'findProperty':
                return null;
            case 'noAccount':
            case 'personalDetails':
                // require postcode to have looked up or enter property
                return hasPostcode ? null : 'findProperty';
            case 'chimneyDetails':
            case 'chimneyList':
            case 'confirmPrice':
            case 'selectDateAndTime':
            case 'summary':
                // these later steps require postcode, customer's name and at least one chimney
                if (!hasPostcode) return 'findProperty';
                if (!hasName) return 'personalDetails';
                if (!hasChimneys) return 'chimneyDetails';
                return null;
            case 'thanks':
            default:
                return null;
        }
    }

    // Ensure the current step is valid. If not, redirect to the returned step.
    // Returns the redirect step name or null if current step is valid.
    ensureValidStep() {
        const { stepName } = this.state;
        const redirectStep = this.validateBookingRequest();
        if (redirectStep && redirectStep !== stepName) {
            this.goToStep(redirectStep);
            return redirectStep;
        }
        return null;
    }

    goToStep(stepName, param) {
        this.props.history.push(`/${stepName}` + (param ? `/${param}` : ''));
    }

    updateFields(newValues, callback) {
        const bookingRequest = { ...this.state.bookingRequest };
        for (let fieldName in newValues) {
            const newValue = newValues[fieldName];
            bookingRequest[fieldName] = newValue;

            if (fieldName == 'customerFirstName' || fieldName == 'customerLastName') {
                bookingRequest.billingName = `${bookingRequest.customerFirstName} ${bookingRequest.customerLastName}`;
            }
            else if (fieldName == 'postcode') {
                bookingRequest.billingPostcode = bookingRequest.postcode;
            }
        }
        this.debounceSaveToLocalStorage();
        this.setState({ bookingRequest }, callback);
    }

    debounceSaveToLocalStorage() {
        clearTimeout(this.saveToLocalStorageTimeout);
        this.saveToLocalStorageTimeout = setTimeout(() => {
            this.saveToLocalStorage(this.state.bookingRequest);
        }, 250);
    }
    
    saveToLocalStorage(bookingRequest) {
        try {
            const json = JSON.stringify(bookingRequest);
            localStorage.setItem('appt', json);
        } catch (error) {
            console.log(error);
        }
    }
    
    loadFromLocalStorage() {
        try {
            const json = localStorage.getItem('appt');
            const bookingRequest = JSON.parse(json);
            return bookingRequest;
        } catch (error) {
            console.log(error);
        }
    }

    async tryLoadAccountInfo() {
        return new Promise(async (resolve) => {
            let { bookingRequest } = this.state;
            let accountInfo = null;

            // Load
            if (bookingRequest.postcode) {
                accountInfo = await API.call('book/get-account-info', bookingRequest);
            }
            this.setState({ accountInfo, isLoading: false });

            // Update globally
            this.props.fns.setAccountInfo(accountInfo);
            
            // If not found, go to error page
            if (!accountInfo && bookingRequest && bookingRequest.postcode && this.props.stepName != 'findProperty') {
                this.goToStep('findProperty');
            }
            
            // Return found true/false
            resolve(!!accountInfo);
        });
    }

    startAgain(callback) {
        const bookingRequest = {
            version: App.VERSION,
            chimneyTypeIDs: []
        };
        this.saveToLocalStorage(bookingRequest);
        this.setState({ bookingRequest }, callback);
    }

    render() {
        const { isLoading } = this.state;
        const { stepName } = this.props;
        if (isLoading) {
            return (<Loader />);
        }
        return this.renderStep(stepName);
    }

    renderStep(stepName) {
        const { bookingRequest, accountInfo } = this.state;
        const { stepParam } = this.props;
        const subProps = {
            ...this.props,
            fns: {
                ...this.props.fns,
                tryLoadAccountInfo: this.tryLoadAccountInfo
            },
            bookingRequest,
            accountInfo
        };

        switch (stepName) {
            case 'findProperty':
                return (
                    <FindProperty
                        {...subProps}
                        onChange={(newValues) => this.updateFields(newValues)}
                        onNextClicked={() => {
                            this.goToStep('personalDetails');
                        }}
                    />
                );
            case 'noAccount':
                return (
                    <NoAccount
                        {...subProps}
                        onBackClicked={() => {
                            this.goToStep('findProperty');
                        }}
                    />
                );
            case 'personalDetails':
                return (
                    <PersonalDetails
                        {...subProps}
                        onChange={(newValues) => this.updateFields(newValues)}
                        onNextClicked={() => {
                            if (bookingRequest.chimneyTypeIDs.length > 0) {
                                this.goToStep('chimneyList');
                            } else {
                                this.goToStep('chimneyDetails');
                            }
                        }}
                        onBackClicked={() => {
                            this.goToStep('findProperty');
                        }}
                    />
                );
            case 'chimneyDetails':
                const index = (stepParam ? parseInt(stepParam) : null);
                return (
                    <ChimneyDetails
                        {...subProps}
                        index={index}
                        onChimneyTypeSelected={async (chimneyTypeID) => {
                            // Add new chimney or amend an existing one
                            const chimneyTypeIDs = [...bookingRequest.chimneyTypeIDs];
                            if (index != null) {
                                chimneyTypeIDs[index] = chimneyTypeID;
                            } else {
                                chimneyTypeIDs.push(chimneyTypeID);
                            }
                            this.updateFields({ chimneyTypeIDs });
                            this.goToStep('chimneyList');
                        }}
                        onBackClicked={() => {
                            if (index != null) { 
                                // If editing an existing chimney, go back to the 'any more?' screen.
                                this.goToStep('chimneyList');
                            } else {
                                // Otherwise, go back to the personal details screen
                                this.goToStep('personalDetails');
                            }
                        }}
                    />
                );
            case 'chimneyList':
                return (
                    <ChimneyList
                        {...subProps}
                        onChangeClicked={(index) => this.goToStep('chimneyDetails', `${index}`)}
                        onAddClicked={(index) => this.goToStep('chimneyDetails')}
                        onRemoveClicked={(index) => {
                            const chimneyTypeIDs = [...bookingRequest.chimneyTypeIDs];
                            chimneyTypeIDs.splice(index, 1);
                            this.updateFields({ chimneyTypeIDs });
                        }}
                        onBackClicked={() => this.goToStep('personalDetails')}
                        onNextClicked={() => this.goToStep('confirmPrice')}
                    />
                );
            case 'confirmPrice':
                return (
                    <ConfirmPrice
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onNextClicked={() => {
                            this.goToStep('selectDateAndTime');
                        }}
                        onBackClicked={() => {
                            this.goToStep('chimneyList');
                        }}
                    />
                );
            case 'selectDateAndTime':
                return (
                    <SelectDateAndTime
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onNextClicked={() => {
                            this.goToStep('summary');
                        }}
                        onBackClicked={() => {
                            this.goToStep('confirmPrice');
                        }}
                    />
                );
            case 'summary':
                return (
                    <Summary
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onNextClicked={() => {
                            this.startAgain(() => {
                                this.goToStep('thanks');
                            });
                        }}
                        onBackClicked={() => {
                            this.goToStep('selectDateAndTime');
                        }}
                    />
                );
            case 'thanks':
                return (
                    <Thanks
                        {...subProps}
                        onChange={(newValues, callback) => this.updateFields(newValues, callback)}
                        onStartAgainClicked={() => {
                            this.goToStep('findProperty');
                        }}
                    />
                );
                break;
        }
        return null;
    }
}

export default withRouter(BookAppt);